<template>
  <div class="prvhl" aria-label="Version history">
    <p v-if="!rows.length" class="prvhl__empty">No versions yet</p>

    <template v-else>
      <div
        v-for="row in rows"
        :key="row.key"
        class="prvhl__row"
        :class="[
          `prvhl__row--${row.status}`,
          { 'prvhl__row--selected': row.selected },
        ]"
        role="group"
        tabindex="0"
        :aria-label="`${row.versionLabel}, ${row.statusLabel}`"
        :aria-current="row.selected ? 'true' : null"
        @click="onSelectRow(row, $event)"
        @keydown.enter.self.prevent="onSelectRow(row)"
        @keydown.space.self.prevent="onSelectRow(row)"
      >
        <div class="prvhl__name">
          <div v-if="row.showVersionLabel" class="prvhl__title-row">
            <span class="prvhl__version">{{ row.versionLabel }}</span>
          </div>

          <div class="prvhl__meta">
            <span class="prvhl__badge" :class="`prvhl__badge--${row.status}`">
              {{ row.statusLabel }}
            </span>
            <span class="prvhl__dates">{{ row.dateRangeLabel }}</span>
            <span class="prvhl__amount">{{ row.amountLabel }}</span>
          </div>
        </div>

        <!-- Draft: Discard · Publish Draft · Edit Draft (Figma 3439:7318) -->
        <template v-if="row.status === 'draft'">
          <button
            type="button"
            class="prvhl__btn prvhl__btn--tertiary-error"
            @click="$emit('discard-draft', row.payload)"
          >
            Discard
          </button>
          <button
            type="button"
            class="prvhl__btn prvhl__btn--primary"
            @click="$emit('publish-draft', row.payload)"
          >
            Publish Draft
          </button>
          <button
            type="button"
            class="prvhl__btn prvhl__btn--secondary-color"
            @click="$emit('edit-draft', row.payload)"
          >
            Edit Draft
          </button>
        </template>

        <!-- Every non-voided, non-draft row: usage · Correct · ⋮. Correct is disabled
             when in use; Void is disabled when in use or the last remaining live version. -->
        <template v-else-if="row.showActions">
          <span v-if="row.showUsage" class="prvhl__usage">{{ row.usageLabel }}</span>

          <div class="prvhl__trailing">
            <EXTooltip
              v-if="row.showCorrect && row.inUse"
              placement="top"
              :title="row.inUseTitle"
            >
              <span class="prvhl__correct-trigger">
                <button
                  type="button"
                  class="prvhl__btn prvhl__btn--secondary-color"
                  :disabled="row.inUse"
                  @click="$emit('correct', row.payload)"
                >
                  Correct
                </button>
              </span>
            </EXTooltip>
            <button
              v-else-if="row.showCorrect"
              type="button"
              class="prvhl__btn prvhl__btn--secondary-color"
              @click="$emit('correct', row.payload)"
            >
              Correct
            </button>

            <DropDownMenu
              :dropdownItems="row.menuItems"
              :is-open="false"
              :width="160"
              tipSide="right"
              :translate-y-offset="8"
              :translate-x-offset="0"
              @onClick="onMenuAction(row, $event)"
            >
              <template #activator>
                <button
                  type="button"
                  class="prvhl__menu"
                  :aria-label="`Actions for ${row.versionLabel}`"
                >
                  <img class="prvhl__menu-icon" :src="dotsIcon" alt="" width="24" height="24" />
                </button>
              </template>
            </DropDownMenu>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed } from "vue";
import DropDownMenu from "@components/DropDownMenu/DropDownMenu.vue";
import EXTooltip from "@components/tooltip/EXTooltip.vue";
import { formatPayRangeAmount } from "@components/utils/formatPayRangeAmount";
import { localTodayIso } from "@components/PayRangeTimelineAndList/payRangeTimelineUtils";
import { resolvePayRangeVersionStatus } from "@components/PayRangeVersionRow/payRangeVersionRowUtils";
import dotsIcon from "./icon-dots-vertical.svg";
import { formatFairPayDate } from "@components/utils/fairPayFormat";

const props = defineProps({
  /**
   * One row per pay range version, newest-first input order is optional
   * (the list re-sorts by start date). Shape:
   * { key, versionNumber, versionLabel, status: 'draft'|'voided'|<any other = In Force>,
   *   startDate, endDate, minAmount, maxAmount, usageCount }
   * The State badge (Lapsed / Current / Future) is computed from startDate / endDate.
   */
  versions: { type: Array, default: () => [] },
  unitSuffix: { type: String, default: "USD/year" },
  selectedVersion: { type: [Number, String], default: null },
  /** Reference day for the State badges; defaults to the local calendar day. */
  today: { type: [String, Date], default: null },
});

const emit = defineEmits([
  "correct",
  "void",
  "discard-draft",
  "publish-draft",
  "edit-draft",
  "select",
]);

const STATUS_LABEL = {
  current: "Current",
  passed: "Lapsed",
  draft: "Draft",
  voided: "Voided",
  future: "Future",
};

/** Draft and Voided are stored Statuses; every other row shows the State its dates give today. */
function rowStatus(version, today) {
  if (version.status === "draft" || version.status === "voided") return version.status;
  const state = resolvePayRangeVersionStatus(version.startDate, version.endDate, today);
  return state === "lapsed" ? "passed" : state;
}

const EN_DASH = "\u2013";

function toTime(value) {
  if (value == null || value === "") return null;
  const d = value instanceof Date ? value : new Date(String(value).slice(0, 10));
  const t = d.getTime();
  return Number.isNaN(t) ? null : t;
}

/** "USD/year" → "USD" so amounts render "USD 45K" per Figma. */
const currencyCode = computed(() => String(props.unitSuffix || "").split("/")[0] || "USD");

function formatAmount(n) {
  if (n == null || Number.isNaN(Number(n))) return "";
  return `${currencyCode.value} ${formatPayRangeAmount(n)}`;
}

function usageLabel(usageCount) {
  if (usageCount === 0) return "Not used";
  return `Used by ${usageCount} Pay Plan${usageCount === 1 ? "" : "s"}`;
}

/** Newest first (Figma: Version 3, Version 2, Version 1, …). */
const rows = computed(() => {
  const source = Array.isArray(props.versions) ? props.versions : [];
  const liveCount = source.filter(
    (v) => v.status !== "draft" && v.status !== "voided"
  ).length;
  const lastRemaining = liveCount <= 1;
  const today = props.today ?? localTodayIso();
  return source
    .map((v, index) => {
      const status = rowStatus(v, today);
      const versionNumber = Number(v.versionNumber) || index + 1;
      const usageCount = Math.max(0, Number(v.usageCount) || 0);
      const inUse = usageCount > 0;
      const cannotVoid = inUse || lastRemaining;
      const startLabel = formatFairPayDate(v.startDate);
      const endLabel = formatFairPayDate(v.endDate) || "open";
      const amountLabel = [formatAmount(v.minAmount ?? null), formatAmount(v.maxAmount ?? null)]
        .filter(Boolean)
        .join(` ${EN_DASH} `);
      const inUseTitle = inUse
        ? `In use by ${usageCount} Pay Plan${usageCount === 1 ? "" : "s"}`
        : "";
      const voidDisabledReason = inUse
        ? inUseTitle
        : lastRemaining
          ? "A pay range must keep at least one version"
          : "";
      return {
        key: v.key || `v${versionNumber}`,
        versionNumber,
        startDate: v.startDate ?? null,
        versionLabel: v.versionLabel || `Version ${versionNumber}`,
        status,
        statusLabel: STATUS_LABEL[status] || status,
        dateRangeLabel: startLabel ? `${startLabel} ${EN_DASH} ${endLabel}` : endLabel,
        amountLabel: amountLabel || "—",
        usageLabel: usageLabel(usageCount),
        inUse,
        lastRemaining,
        cannotVoid,
        inUseTitle,
        /** Voided versions are historical records only — no title, no actions. */
        selected:
          props.selectedVersion != null &&
          String(versionNumber) === String(props.selectedVersion),
        showVersionLabel: status !== "voided",
        showActions: status !== "voided",
        showUsage: status !== "voided",
        showCorrect: status !== "voided",
        menuItems: [
          {
            name: "Void",
            value: "void",
            icon: "icons/dropdown/trash.svg",
            disabled: cannotVoid,
            negative: true,
            disabledReason: voidDisabledReason || undefined,
          },
        ],
        payload: { ...v, versionNumber },
      };
    })
    .sort((a, b) => {
      // Voided versions are revoked records — always below the live ones (Figma 3520:20075).
      const voidedA = a.status === "voided" ? 1 : 0;
      const voidedB = b.status === "voided" ? 1 : 0;
      if (voidedA !== voidedB) return voidedA - voidedB;
      const ta = toTime(a.startDate) ?? 0;
      const tb = toTime(b.startDate) ?? 0;
      if (tb !== ta) return tb - ta;
      return b.versionNumber - a.versionNumber;
    });
});

/** Row buttons and the ⋮ menu keep their own action; the rest of the row selects it. */
function onSelectRow(row, event) {
  if (event?.target?.closest?.("button, a, .prvhl__trailing")) return;
  emit("select", row.versionNumber, row.payload);
}

function onMenuAction(row, item) {
  if (item?.value !== "void" || row.cannotVoid) return;
  emit("void", row.payload);
}
</script>

<style scoped>
/* Tokens from Figma PayRangeCard VersionList node 3439:6941 — Untitled UI light. */
.prvhl {
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 100%;
  min-width: 0;
  overflow: hidden;
}

.prvhl__empty {
  margin: 0;
  padding: 12px 0;
  font-family: Inter, sans-serif;
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
  color: #667085;
}

/* PayRangeVersionCard — node 3439:7318 */
.prvhl__row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 14px;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
  box-sizing: border-box;
  cursor: pointer;
  transition:
    border-color 0.12s ease,
    background-color 0.12s ease,
    box-shadow 0.12s ease;
}

.prvhl__row:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}

.prvhl__row:hover {
  border: 1px solid #d0d5dd;
  background: #f9fafb;
  box-shadow: 0 1px 2px 0 rgba(15, 23, 41, 0.08);
}

.prvhl__row--selected,
.prvhl__row--selected:hover {
  border: 1.5px solid #7f56d9;
  background: #faf8fe;
  box-shadow: none;
}

/* PayRangeVersionNameCard */
.prvhl__name {
  display: flex;
  flex: 1 1 0;
  align-items: center;
  gap: 10px;
  min-width: 0;
  overflow: hidden;
}

.prvhl__title-row {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  overflow: hidden;
}

.prvhl__version {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: normal;
  color: #101828;
  white-space: nowrap;
}

.prvhl__meta {
  display: flex;
  align-items: center;
  gap: 25px;
  min-width: 0;
  overflow: hidden;
}

.prvhl__badge {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  padding: 2px 8px;
  border: 1px solid transparent;
  border-radius: 9999px;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  text-align: center;
  white-space: nowrap;
}

.prvhl__badge--current {
  background: #ecfdf3;
  border-color: #abefc6;
  color: #067647;
}

.prvhl__badge--passed {
  background: #f9fafb;
  border-color: #eaecf0;
  color: #344054;
}

.prvhl__badge--draft {
  background: var(--component-colors-utility-fuchsia-utility-fuchsia-50, #fdf4ff);
  border: 1px dashed var(--component-colors-utility-fuchsia-utility-fuchsia-500, #d444f1);
  color: var(--component-colors-utility-fuchsia-utility-fuchsia-700, #9f1ab1);
}

.prvhl__badge--voided {
  background: #fef3f2;
  border-color: #fecdca;
  color: #b42318;
}

/* Published but not yet effective — PayRangeVersionBar "Future" (light blue). */
.prvhl__badge--future {
  background: #f0f9ff;
  border-color: #b2ddff;
  color: #026aa2;
}

.prvhl__dates {
  font-family: Inter, sans-serif;
  font-size: 13px;
  font-weight: 400;
  line-height: normal;
  color: #667085;
  white-space: nowrap;
}

.prvhl__amount,
.prvhl__usage {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
  color: #343e55;
  white-space: nowrap;
}

.prvhl__amount {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.prvhl__usage {
  flex-shrink: 0;
}

.prvhl__trailing {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

/* Tooltip trigger wraps the disabled Correct button: a disabled button swallows
   pointer events, so the wrapper owns hover and the button stays non-interactive. */
.prvhl__correct-trigger {
  display: inline-flex;
  align-items: center;
  cursor: not-allowed;
}

.prvhl__correct-trigger .prvhl__btn:disabled {
  pointer-events: none;
}

/* Buttons/Button — shared Untitled UI metrics */
.prvhl__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 10px 16px;
  border: 1px solid transparent;
  border-radius: 8px;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  box-sizing: border-box;
}

.prvhl__btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.prvhl__btn--primary {
  background: #7f56d9;
  border-color: #7f56d9;
  color: #fff;
}

.prvhl__btn--primary:hover:not(:disabled) {
  background: #6941c6;
  border-color: #6941c6;
}

.prvhl__btn--secondary-color {
  background: #fff;
  border-color: #d6bbfb;
  color: #6941c6;
}

.prvhl__btn--secondary-color:hover:not(:disabled) {
  background: #f9f5ff;
}

.prvhl__btn--tertiary-error {
  padding: 0;
  background: transparent;
  border-color: transparent;
  box-shadow: none;
  color: #b42318;
}

.prvhl__btn--tertiary-error:hover:not(:disabled) {
  color: #912018;
}

/* MenuIcon — node 3565:5163 (40x40 box, 24x24 glyph) */
.prvhl__menu {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
}

.prvhl__menu:hover {
  filter: drop-shadow(0 4px 2px rgba(0, 0, 0, 0.05));
}

.prvhl__menu:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}

.prvhl__menu-icon {
  display: block;
  width: 24px;
  height: 24px;
  object-fit: contain;
}

@media (max-width: 900px) {
  .prvhl__row {
    flex-wrap: wrap;
  }

  .prvhl__meta {
    flex-wrap: wrap;
    gap: 10px;
  }
}
</style>
